'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import type { PageFlip } from 'page-flip';

const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337';

/** Nombre de pages en accès libre — au-delà, réservé aux membres (RGPD) */
const PUBLIC_PAGES = 11;

interface Manifest {
  pageCount: number;
  width: number;
  height: number;
  pages: string[];
}

const ZOOM_MAX = 3;
const ZOOM_STEP = 0.5;

/** Pages visibles pour un index donné (couverture et dos seuls, le reste en double page) */
function spreadFor(index: number, total: number): number[] {
  if (index <= 0) return [0];
  const left = index % 2 === 1 ? index : index - 1;
  return left + 1 <= total - 1 ? [left, left + 1] : [left];
}

function LockPanel({ compact = false }: { compact?: boolean }) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 bg-gradient-to-b from-[#006C8F] to-[#005570] text-white text-center p-8">
      <div className="w-14 h-14 rounded-full bg-[#FFC72C] flex items-center justify-center">
        <svg
          className="w-7 h-7 text-[#006C8F]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>
      <p
        className={`font-bold ${compact ? 'text-lg' : 'text-2xl'}`}
        style={{ fontFamily: 'var(--font-playfair), serif' }}
      >
        La suite est réservée aux membres
      </p>
      <p className="text-white/75 text-sm max-w-xs">
        Connectez-vous avec votre compte membre pour lire l&apos;intégralité du magazine.
      </p>
      <Link
        href="/login"
        className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 bg-[#FFC72C] text-[#006C8F] font-bold rounded-full hover:bg-white transition-colors text-sm"
      >
        Se connecter
      </Link>
    </div>
  );
}

export default function HistoireFlipbook() {
  const { status } = useSession();
  const isMember = status === 'authenticated';

  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState(false);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [dims, setDims] = useState<{ pw: number; ph: number } | null>(null);

  const bookRef = useRef<HTMLDivElement>(null);
  const pageFlipRef = useRef<PageFlip | null>(null);
  const currentPageRef = useRef(0);
  const panRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  // Pages 1-10 servies en direct par Strapi (publiques). La page 11 est servie
  // floutée (visages) aux visiteurs, en clair via le proxy authentifié aux membres.
  // Toutes les autres passent par le proxy Next (cookie de session).
  const pageSrc = useCallback(
    (index: number, pages: string[]) => {
      if (index < 10) return `${STRAPI_URL}/histoire/${pages[index]}`;
      if (!isMember && index === 10) return `${STRAPI_URL}/histoire/public-pages/page-011.jpg`;
      return `/api/histoire/${pages[index]}`;
    },
    [isMember]
  );

  // Récupération du manifest (liste des pages générées depuis le PDF)
  useEffect(() => {
    fetch(`${STRAPI_URL}/histoire/manifest.json`)
      .then((res) => {
        if (!res.ok) throw new Error('manifest introuvable');
        return res.json();
      })
      .then((data: Manifest) => setManifest(data))
      .catch(() => setError(true));
  }, []);

  // Desktop = flipbook en pop-in, mobile/tablette = défilement vertical
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    setIsDesktop(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Dimensions d'une page dans la pop-in : on remplit la hauteur de l'écran
  const computeDims = useCallback(() => {
    if (!manifest) return null;
    const ratio = manifest.height / manifest.width;
    const availW = window.innerWidth - 140; // marges + flèches
    const availH = window.innerHeight - 130; // barre d'outils
    const pw = Math.floor(Math.min(availW / 2, availH / ratio));
    return { pw, ph: Math.floor(pw * ratio) };
  }, [manifest]);

  // Ouverture / fermeture de la pop-in
  const openReader = useCallback(() => {
    const d = computeDims();
    if (d) {
      setDims(d);
      setIsOpen(true);
    }
  }, [computeDims]);

  const closeReader = useCallback(() => {
    setIsOpen(false);
    setZoom(1);
  }, []);

  // Verrouillage du scroll + recalcul à la redimension quand la pop-in est ouverte
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onResize = () => {
      const d = computeDims();
      if (d) setDims(d);
    };
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, computeDims]);

  // Nombre de pages réellement affichées + éventuelle page « verrou »
  const visibleCount = manifest
    ? isMember
      ? manifest.pageCount
      : Math.min(PUBLIC_PAGES, manifest.pageCount)
    : 0;
  const hasLockPage = manifest ? !isMember && visibleCount < manifest.pageCount : false;
  const bookTotal = visibleCount + (hasLockPage ? 1 : 0);

  // Initialisation du flipbook (mode HTML, taille fixe → pli exactement au centre)
  useEffect(() => {
    if (!isOpen || !manifest || !dims || !bookRef.current) return;

    let cancelled = false;
    let instance: PageFlip | null = null;
    const el = bookRef.current;

    (async () => {
      // Le build UMD de page-flip peut exposer ses exports sous `default` selon le bundler
      const mod = (await import('page-flip')) as unknown as Record<string, unknown>;
      const resolved = (mod.PageFlip ? mod : mod.default) as { PageFlip: typeof PageFlip };
      if (cancelled || !el.isConnected) return;

      instance = new resolved.PageFlip(el, {
        width: dims.pw,
        height: dims.ph,
        size: 'fixed',
        showCover: true,
        usePortrait: false,
        maxShadowOpacity: 0.4,
        flippingTime: 600,
        mobileScrollSupport: false,
      });
      instance.loadFromHTML(el.querySelectorAll('.fb-page'));
      instance.on('flip', (e) => {
        currentPageRef.current = e.data as number;
        setCurrentPage(e.data as number);
      });
      if (currentPageRef.current > 0) {
        instance.turnToPage(Math.min(currentPageRef.current, bookTotal - 1));
      }
      pageFlipRef.current = instance;
    })();

    return () => {
      cancelled = true;
      try {
        instance?.destroy();
      } catch {
        // le flipbook peut ne pas être encore monté
      }
      pageFlipRef.current = null;
    };
  }, [isOpen, manifest, dims, bookTotal]);

  const goTo = useCallback(
    (dir: 1 | -1) => {
      const pf = pageFlipRef.current;
      if (!pf || !bookTotal) return;
      if (zoom > 1) {
        // en zoom : saut direct de double page, sans animation
        const cur = spreadFor(currentPageRef.current, bookTotal);
        const target = dir === 1 ? cur[cur.length - 1] + 1 : cur[0] - 1;
        if (target < 0 || target > bookTotal - 1) return;
        pf.turnToPage(target);
        currentPageRef.current = target;
        setCurrentPage(target);
      } else {
        if (dir === 1) pf.flipNext();
        else pf.flipPrev();
      }
    },
    [bookTotal, zoom]
  );

  // Navigation clavier dans la pop-in
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (zoom > 1) setZoom(1);
        else closeReader();
      } else if (e.key === 'ArrowRight') goTo(1);
      else if (e.key === 'ArrowLeft') goTo(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, zoom, goTo, closeReader]);

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border-2 border-[#FFC72C]/10 p-12 text-center">
        <p className="text-gray-500 text-lg">
          Le magazine n&apos;est pas disponible pour le moment. Revenez bientôt !
        </p>
      </div>
    );
  }

  if (!manifest || isDesktop === null || status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="w-12 h-12 border-4 border-[#006C8F]/20 border-t-[#006C8F] rounded-full animate-spin" />
        <p className="text-[#006C8F] font-medium">Chargement du magazine…</p>
      </div>
    );
  }

  // ---- Version mobile / tablette : défilement vertical ----
  if (!isDesktop) {
    return (
      <div className="space-y-4">
        {manifest.pages.slice(0, visibleCount).map((p, i) => (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            key={p}
            src={pageSrc(i, manifest.pages)}
            alt={`Page ${i + 1} du magazine`}
            loading={i < 2 ? 'eager' : 'lazy'}
            width={manifest.width}
            height={manifest.height}
            className="w-full h-auto rounded-xl shadow-lg border border-gray-200 bg-white"
          />
        ))}
        {hasLockPage && (
          <div className="rounded-xl overflow-hidden shadow-lg aspect-[3/2]">
            <LockPanel compact />
          </div>
        )}
      </div>
    );
  }

  const spread = spreadFor(currentPage, bookTotal);
  const spreadOnLock = spread.some((i) => i >= visibleCount);

  // ---- Version desktop : couverture + lecture en pop-in ----
  return (
    <>
      {/* Aperçu : couverture + bouton de lecture */}
      <div className="flex flex-col items-center gap-8">
        <button
          onClick={openReader}
          className="group relative focus:outline-none"
          aria-label="Ouvrir le magazine"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={pageSrc(0, manifest.pages)}
            alt="Couverture du magazine Le Rouge et le Vert"
            className="h-[480px] w-auto rounded-lg shadow-2xl border border-gray-200 transition-transform duration-300 group-hover:scale-[1.02]"
          />
          <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 group-hover:bg-black/30 transition-colors">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 px-5 py-2.5 bg-[#FFC72C] text-[#006C8F] font-bold rounded-full shadow-lg text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              Feuilleter
            </span>
          </span>
        </button>

        <div className="flex flex-col items-center gap-3">
          <button
            onClick={openReader}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#006C8F] text-white font-bold rounded-full hover:bg-[#005570] transition-colors shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            Lire le magazine
          </button>
          {!isMember && (
            <p className="text-gray-500 text-sm">
              Extrait libre : pages 1 à {PUBLIC_PAGES} —{' '}
              <Link
                href="/login"
                className="text-[#006C8F] font-semibold underline hover:text-[#005570]"
              >
                connectez-vous
              </Link>{' '}
              pour lire l&apos;intégralité.
            </p>
          )}
        </div>
      </div>

      {/* Pop-in de lecture */}
      {isOpen && dims && (
        <div className="fixed inset-0 z-[100] bg-[#00293a] flex flex-col">
          {/* Barre d'outils */}
          <div className="flex items-center justify-between px-6 py-3 text-white flex-shrink-0">
            <span
              className="font-bold text-lg hidden md:block"
              style={{ fontFamily: 'var(--font-playfair), serif' }}
            >
              Le Rouge et le Vert
            </span>

            <div className="flex items-center gap-3">
              <button
                onClick={() => goTo(-1)}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/25 transition-colors disabled:opacity-30"
                disabled={currentPage <= 0}
                aria-label="Page précédente"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <span className="font-semibold text-sm tabular-nums min-w-40 text-center">
                {spreadOnLock
                  ? 'Réservé aux membres'
                  : `Page ${spread.map((i) => i + 1).join('-')} / ${manifest.pageCount}`}
              </span>

              <button
                onClick={() => goTo(1)}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/25 transition-colors disabled:opacity-30"
                disabled={spread[spread.length - 1] >= bookTotal - 1}
                aria-label="Page suivante"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              <span className="w-px h-6 bg-white/20 mx-1" />

              <button
                onClick={() => setZoom((z) => Math.max(1, z - ZOOM_STEP))}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/25 transition-colors disabled:opacity-30"
                disabled={zoom <= 1}
                aria-label="Zoom arrière"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                  />
                </svg>
              </button>
              <span className="font-semibold text-sm tabular-nums w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/25 transition-colors disabled:opacity-30"
                disabled={zoom >= ZOOM_MAX}
                aria-label="Zoom avant"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                  />
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={closeReader}
                className="p-2.5 rounded-full bg-[#FFC72C] text-[#006C8F] hover:bg-white transition-colors"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Zone de lecture */}
          <div className="relative flex-1 min-h-0">
            {/* Flipbook (toujours monté, masqué pendant le zoom) */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ visibility: zoom > 1 ? 'hidden' : 'visible' }}
            >
              <div key={`${dims.pw}x${dims.ph}-${bookTotal}`} ref={bookRef} className="select-none">
                {manifest.pages.slice(0, visibleCount).map((p, i) => (
                  <div
                    key={p}
                    className="fb-page bg-white"
                    style={{ width: dims.pw, height: dims.ph }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={pageSrc(i, manifest.pages)}
                      alt={`Page ${i + 1}`}
                      draggable={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </div>
                ))}
                {hasLockPage && (
                  <div className="fb-page" style={{ width: dims.pw, height: dims.ph }}>
                    <LockPanel />
                  </div>
                )}
              </div>
            </div>

            {/* Vue zoomée : double page agrandie, déplaçable à la souris */}
            {zoom > 1 && (
              <div
                ref={panRef}
                className="absolute inset-0 overflow-auto cursor-grab active:cursor-grabbing"
                onMouseDown={(e) => {
                  const el = panRef.current;
                  if (!el) return;
                  dragState.current = {
                    x: e.clientX,
                    y: e.clientY,
                    left: el.scrollLeft,
                    top: el.scrollTop,
                  };
                }}
                onMouseMove={(e) => {
                  const el = panRef.current;
                  const d = dragState.current;
                  if (!el || !d || e.buttons !== 1) return;
                  el.scrollLeft = d.left - (e.clientX - d.x);
                  el.scrollTop = d.top - (e.clientY - d.y);
                }}
                onMouseUp={() => (dragState.current = null)}
                onMouseLeave={() => (dragState.current = null)}
              >
                <div
                  className="flex justify-center items-start mx-auto py-4"
                  style={{
                    width: Math.max(spread.length * dims.pw * zoom + 32, 0),
                    minWidth: '100%',
                  }}
                >
                  {spread.map((i) =>
                    i < visibleCount ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img
                        key={i}
                        src={pageSrc(i, manifest.pages)}
                        alt={`Page ${i + 1}`}
                        draggable={false}
                        className="bg-white shadow-2xl"
                        style={{ width: dims.pw * zoom, height: 'auto' }}
                      />
                    ) : (
                      <div key={i} style={{ width: dims.pw * zoom, height: dims.ph * zoom }}>
                        <LockPanel />
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-white/40 text-xs pb-2 flex-shrink-0">
            Flèches ◀ ▶ pour tourner les pages · Échap pour{' '}
            {zoom > 1 ? 'quitter le zoom' : 'fermer'} · cliquez sur le coin d&apos;une page ou
            faites-la glisser
          </p>
        </div>
      )}
    </>
  );
}
